/**
 * The showroom QR sheet as a PDF: A4 portrait, the same card layout as the
 * preview (showroom-sheet.ts), codes drawn as vector rectangles so they stay
 * crisp at any print size.
 *
 * Text uses jsPDF's built-in Helvetica. Its encoding has no Arabic glyphs (nor
 * most non-Latin scripts), so the caller passes the call to action in English
 * when the vendor works in Arabic; product names are printed as they are, with
 * characters the font cannot draw dropped (pdfSafeText). The Print button
 * renders through the browser and has no such limit.
 */
import { CardBoxes, QR_QUIET, SheetLayout, clampLines, cardsPerPage, pdfSafeText, ptToMm, qrMatrix, qrRuns, sheetPages } from './showroom-sheet';

export interface SheetCard {
    key: string;
    name: string;
    imageUrl: string | null;
    /** Already formatted ("€42.90 / m²"), or null — never a made-up price. */
    price: string | null;
    /** What the code opens. */
    url: string;
}

const GREY_FRAME = 212;
const PLACEHOLDER = 242;

/**
 * A product photo as a square JPEG data URL (centre-cropped, like the
 * preview's object-fit: cover), or null when it cannot be read — most often a
 * CDN that does not allow cross-origin reads. The card then gets a plain
 * placeholder instead of failing the whole sheet.
 */
export async function loadSquareImage(url: string, px = 640): Promise<string | null> {
    try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) return null;
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                const i = new Image();
                i.onload = () => resolve(i);
                i.onerror = reject;
                i.src = objectUrl;
            });
            const side = Math.min(img.naturalWidth, img.naturalHeight);
            if (!side) return null;
            const canvas = document.createElement('canvas');
            canvas.width = px;
            canvas.height = px;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, px, px);
            ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, px, px);
            return canvas.toDataURL('image/jpeg', 0.86);
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    } catch {
        return null;
    }
}

/** Build the PDF. Resolves with the document and how many photos had to be left out. */
export async function buildShowroomPdf({
    cards,
    layout,
    cta,
    storeName,
    loadImage = loadSquareImage,
}: {
    cards: SheetCard[];
    layout: SheetLayout;
    cta: string;
    storeName?: string | null;
    loadImage?: (url: string) => Promise<string | null>;
}) {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const images = await Promise.all(cards.map((c) => (c.imageUrl ? loadImage(c.imageUrl) : Promise.resolve(null))));
    const missingImages = cards.filter((c, i) => c.imageUrl && !images[i]).length;
    const per = cardsPerPage(layout);

    sheetPages(cards.length, layout).forEach((page, p) => {
        if (p > 0) doc.addPage();
        page.forEach((boxes, i) => {
            const index = p * per + i;
            drawCard(doc, boxes, cards[index], images[index], layout, pdfSafeText(cta), pdfSafeText(storeName || ''));
        });
    });

    return { doc, missingImages };
}

function drawCard(doc: any, boxes: CardBoxes, card: SheetCard, image: string | null, layout: SheetLayout, cta: string, store: string) {
    const { frame, photo, qr, text } = boxes;

    // The cut line.
    doc.setDrawColor(GREY_FRAME);
    doc.setLineWidth(0.2);
    doc.roundedRect(frame.x, frame.y, frame.w, frame.h, 2, 2, 'S');

    // Photo, or a quiet placeholder.
    if (image) {
        doc.addImage(image, 'JPEG', photo.x, photo.y, photo.w, photo.h, undefined, 'FAST');
    } else {
        doc.setFillColor(PLACEHOLDER);
        doc.rect(photo.x, photo.y, photo.w, photo.h, 'F');
    }

    // The code: one rectangle per horizontal run of dark modules.
    const matrix = qrMatrix(card.url);
    if (matrix.length) {
        const m = qr.w / (matrix.length + 2 * QR_QUIET);
        doc.setFillColor(0);
        for (const run of qrRuns(matrix)) {
            // A hair taller than a module so rows meet without a seam in some viewers.
            doc.rect(qr.x + (run.col + QR_QUIET) * m, qr.y + (run.row + QR_QUIET) * m, run.len * m, m + 0.01, 'F');
        }
    }

    // Words: name and price from the top, call to action and store from the bottom.
    doc.setFont('helvetica', 'normal');
    const type = layout.type;
    let y = text.y;

    doc.setFontSize(type.name);
    doc.setTextColor(17);
    const nameLines = clampLines(doc.splitTextToSize(pdfSafeText(card.name), text.w) as string[], 2);
    nameLines.forEach((line, i) => {
        y = text.y + ptToMm(type.name) * (0.9 + i * 1.2);
        doc.text(line, text.x, y);
    });

    if (card.price) {
        doc.setFontSize(type.price);
        y += ptToMm(type.price) * 1.35;
        doc.text(pdfSafeText(card.price), text.x, y);
    }

    let bottom = text.y + text.h - 0.4;
    if (store) {
        doc.setFontSize(type.store);
        doc.setTextColor(140);
        doc.text(clampLines(doc.splitTextToSize(store, text.w) as string[], 1)[0], text.x, bottom);
        bottom -= ptToMm(type.store) * 1.3;
    }
    doc.setFontSize(type.cta);
    doc.setTextColor(85);
    doc.text(clampLines(doc.splitTextToSize(cta, text.w) as string[], 1)[0], text.x, bottom);
}

/**
 * Print the sheet through the browser: the pages' markup in a hidden frame
 * with an A4 page rule — the app around it never reaches the printer. Unlike
 * the PDF, any script prints (the browser draws the text).
 */
export function printSheetPages(pages: HTMLElement[], title: string): void {
    if (!pages.length) return;
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    const win = frame.contentWindow;
    if (!doc || !win) {
        frame.remove();
        return;
    }
    const safeTitle = title.replace(/[<>&]/g, '');
    doc.open();
    doc.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>${safeTitle}</title><style>` +
            '@page{size:A4 portrait;margin:0}html,body{margin:0;padding:0;background:#fff}' +
            '*{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}' +
            '[data-sheet-page]{transform:none!important;margin:0!important;box-shadow:none!important;border:0!important;break-after:page;page-break-after:always}' +
            '[data-sheet-page]:last-child{break-after:auto;page-break-after:auto}' +
            `</style></head><body>${pages.map((p) => p.outerHTML).join('')}</body></html>`,
    );
    doc.close();

    const cleanup = () => setTimeout(() => frame.remove(), 500);
    const images = Array.from(doc.images);
    Promise.all(
        images.map((img) => (img.complete ? null : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
        }))),
    ).then(() => {
        win.onafterprint = cleanup;
        win.focus();
        win.print();
        // Browsers that print synchronously never fire afterprint on a hidden frame.
        setTimeout(() => frame.isConnected && frame.remove(), 60000);
    });
}
